import React, { useMemo } from 'react';
import Select from 'react-select';
import { Loader2 } from 'lucide-react'; // ASSUMING Lucide is available here, based on NewReceipt.

/**
 * Searchable Select Box — Clean, Professional, Gmail-style
 * Fixed: No more "ghost selected" blue background when opening dropdown
 */

const SearchableSelectBox = ({
    name,
    label,
    value,
    onChange,
    options,
    required = false,
    isMulti = false,
    isClearable = !required,
    // ADDED: loading prop
    loading = false,
    isDisabled = false,
    className = '',
    labelClassName = '',
    containerClassName = '',
    placeholder = 'Select...', // Added default placeholder for convenience
    ...props
}) => {
    // Resolve selected option(s) for react-select
    const selectedValue = useMemo(() => {
        if (isMulti) {
            return options.filter(option => (value || []).includes(option.value));
        }
        return options.find(option => option.value === value) || null;
    }, [value, options, isMulti]);

    // Tailwind-powered custom styles — clean & professional
    const customClassNames = {
        control: (state) => `
      !min-h-[42px] !rounded-lg !border !border-gray-medium !shadow-none
      !transition-all !duration-200 !ease-in-out
      ${state.isFocused
                ? '!border-gmail-blue !ring-2 !ring-offset-2 !ring-gmail-blue'
                : '!border-gray-medium hover:!border-gmail-blue'
            }
      ${isDisabled || loading ? '!bg-gray-100 !cursor-not-allowed' : '!bg-white'}
    `,
        valueContainer: () => '!p-0 !pl-3',
        placeholder: () => '!text-gray-400 !text-sm',
        singleValue: () => '!text-text-primary !font-medium',
        multiValue: () => '!bg-gmail-blue/10 !text-gmail-blue !rounded-md !px-2 !py-1 !text-sm',
        multiValueLabel: () => '!text-gmail-blue !font-medium',
        multiValueRemove: () => '!text-gmail-blue hover:!bg-gmail-blue/20 !rounded-r-md !ml-1',
        indicatorsContainer: () => '!pr-2',
        indicatorSeparator: () => '!bg-gray-300',
        dropdownIndicator: () => '!text-gray-500 hover:!text-gmail-blue',
        clearIndicator: () => '!text-gray-500 hover:!text-gmail-red',
        menu: () => '!rounded-lg !shadow-xl !border !border-gray-200 !mt-1 !z-50 !bg-white',
        menuList: () => '!py-1 !max-h-60',
        // CRITICAL FIX: Only highlight on hover/focus — never show selected bg in menu
        option: ({ isFocused }) => `
      !px-4 !py-2.5 !text-sm !cursor-pointer !transition-colors
      ${isFocused ? '!bg-gray-100' : '!bg-white'}
      !text-text-primary
      hover:!bg-gray-100
    `,
        noOptionsMessage: () => '!px-4 !py-3 !text-sm !text-text-secondary text-center',
        // ADDED: Loading message styling
        loadingMessage: () => '!px-4 !py-3 !text-sm !text-text-secondary text-center flex items-center justify-center gap-2',
    };

    // Custom Loading Message Component
    const LoadingMessage = (props) => (
        <div {...props.innerProps} className={customClassNames.loadingMessage()}>
            <Loader2 size={16} className="animate-spin text-gmail-blue" />
            <span>{props.children}</span>
        </div>
    );

    return (
        <div className={`mb-4 ${containerClassName}`}>
            {label && (
                <label htmlFor={name} className={`block text-sm font-medium text-text-secondary mb-1 ${labelClassName}`}>
                    {label}
                    {required && <span className="text-gmail-red ml-1">*</span>}
                </label>
            )}
            <Select
                inputId={name}
                name={name}
                value={selectedValue}
                onChange={(selected) => {
                    if (isMulti) {
                        onChange({ target: { name, value: selected ? selected.map(opt => opt.value) : [] } });
                    } else {
                        onChange({ target: { name, value: selected ? selected.value : '' } });
                    }
                }}
                options={options}
                isClearable={isClearable}
                isMulti={isMulti}
                // MODIFIED: Disable if it's explicitly disabled OR currently loading
                isDisabled={isDisabled || loading}
                // ADDED: Set isLoading prop
                isLoading={loading}
                // ADDED: Set custom loading message component
                components={{ LoadingMessage }}
                loadingMessage={() => 'Loading options...'}
                placeholder={loading ? 'Loading...' : placeholder} // MODIFIED: Change placeholder when loading
                className={`react-select-container ${className}`}
                classNamePrefix="react-select"
                classNames={customClassNames}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={{
                    menuPortal: base => ({ ...base, zIndex: 9999 }),
                }}
                {...props}
            />
        </div>
    );
};

export default SearchableSelectBox;